import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Achievement, UserAchievement } from '../types/social';

let CATALOG_CACHE: Achievement[] | null = null;
let CATALOG_PROMISE: Promise<Achievement[]> | null = null;

async function loadCatalog(): Promise<Achievement[]> {
  if (CATALOG_CACHE) return CATALOG_CACHE;
  if (CATALOG_PROMISE) return CATALOG_PROMISE;
  CATALOG_PROMISE = (async () => {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .order('category', { ascending: true });
    if (error || !data) {
      console.error('loadCatalog error', error);
      CATALOG_PROMISE = null;
      return [];
    }
    CATALOG_CACHE = data as Achievement[];
    return CATALOG_CACHE;
  })();
  return CATALOG_PROMISE;
}

interface Result {
  catalog: Achievement[];
  earned: UserAchievement[];
  earnedSet: Set<string>;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Retorna catálogo (cached) + medalhas conquistadas pelo userId fornecido.
 * Sem userId, retorna apenas catálogo + earned vazio.
 */
export function useAchievements(userId: string | null | undefined): Result {
  const [catalog, setCatalog] = useState<Achievement[]>(CATALOG_CACHE ?? []);
  const [earned, setEarned] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(true);

  const loadEarned = useCallback(async () => {
    if (!userId) {
      setEarned([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('user_achievements')
      .select('user_id, achievement_id, earned_at')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
    if (error) {
      console.error('useAchievements load error', error);
      setEarned([]);
    } else {
      setEarned((data ?? []) as UserAchievement[]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCatalog().then((data) => {
      if (!cancelled) setCatalog(data);
    });
    loadEarned();
    return () => {
      cancelled = true;
    };
  }, [loadEarned]);

  const earnedSet = new Set(earned.map((e) => e.achievement_id));

  return { catalog, earned, earnedSet, loading, refresh: loadEarned };
}
